import React, { useState } from 'react';
import { Star, TrendingUp, Award, Code, Database, Smartphone, Cloud, Settings } from 'lucide-react';

// Add scrollbar-hide utility styles
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

const TechMatrix = ({ skills = [], technologies = [], loading }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredTech, setHoveredTech] = useState(null);

  const categories = ['all', 'Frontend', 'Backend', 'Mobile', 'Database', 'Language', 'DevOps', 'Cloud'];

  const filteredTechnologies = selectedCategory === 'all' 
    ? technologies 
    : technologies.filter(tech => tech.category === selectedCategory);

  // Generate contribution-style grid data
  const generateContributionData = () => {
    const weeks = 52;
    const daysPerWeek = 7;
    const data = [];
    
    for (let week = 0; week < weeks; week++) {
      for (let day = 0; day < daysPerWeek; day++) {
        const intensity = Math.floor(Math.random() * 5);
        const date = new Date();
        date.setDate(date.getDate() - (weeks - week) * 7 - (daysPerWeek - day));
        
        data.push({
          date: date.toISOString().split('T')[0],
          intensity,
          commits: intensity * Math.floor(Math.random() * 3) + 1
        });
      }
    }
    return data;
  };

  const [contributionData] = useState(generateContributionData());

  const getIntensityColor = (intensity) => {
    const colors = [
      'bg-gray-100 dark:bg-gray-800',
      'bg-emerald-200 dark:bg-emerald-900/50',
      'bg-emerald-300 dark:bg-emerald-800/70',
      'bg-emerald-400 dark:bg-emerald-700/80',
      'bg-emerald-500 dark:bg-emerald-600'
    ];
    return colors[intensity] || colors[0];
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Frontend: <Code className="w-4 h-4" />,
      Backend: <Database className="w-4 h-4" />,
      Mobile: <Smartphone className="w-4 h-4" />,
      Database: <Database className="w-4 h-4" />,
      Language: <Code className="w-4 h-4" />,
      DevOps: <Settings className="w-4 h-4" />,
      Cloud: <Cloud className="w-4 h-4" />
    };
    return icons[category] || <Code className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="py-8 sm:py-12 lg:py-16">
        <style>{scrollbarHideStyles}</style>
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
            Technical Skills
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Loading my technical expertise and learning journey...
          </p>
        </div>
        
        {/* Loading State */}
        <div className="flex justify-center items-center py-12 sm:py-16">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 sm:py-12 lg:py-16">
      <style>{scrollbarHideStyles}</style>
      
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
          Technical Skills
        </h2>
        <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          A comprehensive view of my technical expertise, learning journey, and proficiency levels across different technologies and domains.
        </p>
      </div>

      <div className="px-4 sm:px-6 lg:px-8">
        {/* Category Filters - Mobile Only */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 shadow-sm mb-6 lg:hidden">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Filter by Category</h3>
          
          {/* Mobile: Horizontal scrollable buttons */}
          <div className="flex space-x-2 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`flex-shrink-0 flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-600'
                    : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border border-transparent'
                }`}
              >
                {category !== 'all' && getCategoryIcon(category)}
                <span className="font-medium whitespace-nowrap">
                  {category === 'all' ? 'All' : category}
                </span>
                {category !== 'all' && (
                  <span className="text-xs opacity-75 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                    {technologies.filter(t => t.category === category).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Layout: Stacked */}
        <div className="block lg:hidden space-y-6">
          {/* Technology Grid - Mobile */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex flex-col space-y-2 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Technology Stack
                {selectedCategory !== 'all' && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({selectedCategory})
                  </span>
                )}
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredTechnologies.length} technologies
              </div>
            </div>

            {/* Mobile Technology Grid - 2 columns */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {filteredTechnologies.map((tech) => (
                <div
                  key={tech.id}
                  className="group relative p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all duration-300"
                  onTouchStart={() => setHoveredTech(tech.id)}
                  onTouchEnd={() => setHoveredTech(null)}
                >
                  {/* Tech Icon & Name */}
                  <div className="text-center mb-3">
                    <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                      {tech.icon && tech.icon.startsWith('http') ? (
                        <img 
                          src={tech.icon} 
                          alt={`${tech.name} icon`}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzZCNzI4MCIvPgo8dGV4dCB4PSIyMCIgeT0iMjUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPuKapTwvdGV4dD4KPC9zdmc+';
                          }}
                        />
                      ) : (
                        <span className="text-2xl">{tech.icon || '⚡'}</span>
                      )}
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1 truncate">
                      {tech.name}
                    </h4>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate">
                      {tech.category}
                    </div>
                  </div>

                  {/* Proficiency Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">Proficiency</span>
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">
                        {tech.proficiency}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-1000 ease-out"
                        style={{ 
                          width: `${tech.proficiency}%`,
                          backgroundColor: tech.color || '#10b981'
                        }}
                      />
                    </div>
                  </div>

                  {/* Experience */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                      <TrendingUp className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{tech.yearsExp}y exp</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {tech.proficiency >= 90 && (
                        <Award className="w-3 h-3 text-yellow-500" title="Expert Level" />
                      )}
                      {tech.proficiency >= 80 && tech.proficiency < 90 && (
                        <Star className="w-3 h-3 text-blue-500" title="Advanced Level" />
                      )}
                    </div>
                  </div>

                  {/* Hover Effect Overlay */}
                  {hoveredTech === tech.id && (
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-xl pointer-events-none" />
                  )}
                </div>
              ))}
            </div>

            {/* Category Summary - Mobile */}
            {selectedCategory !== 'all' && (
              <div className="mt-4 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">
                  {selectedCategory} Summary
                </h4>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {filteredTechnologies.length}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Tech</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {filteredTechnologies.length > 0 
                        ? Math.round(filteredTechnologies.reduce((sum, tech) => sum + tech.proficiency, 0) / filteredTechnologies.length)
                        : 0}%
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Avg</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {filteredTechnologies.filter(tech => tech.proficiency >= 90).length}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">Expert</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Skills & Activity - Mobile */}
          <div className="grid grid-cols-1 gap-6">
            {/* Core Skills - Mobile */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Core Skills</h3>
              <div className="space-y-3">
                {skills.map((skill) => (
                  <div key={skill.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        {skill.icon && skill.icon.startsWith('http') ? (
                          <img 
                            src={skill.icon} 
                            alt={`${skill.name} icon`}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiByeD0iNCIgZmlsbD0iIzZCNzI4MCIvPgo8dGV4dCB4PSIxMiIgeT0iMTYiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPfCfk6c8L3RleHQ+Cjwvc3ZnPg==';
                            }}
                          />
                        ) : (
                          <span className="text-xl">{skill.icon || '🔧'}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {skill.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {skill.category}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <div className="font-bold text-sm text-gray-900 dark:text-white">
                        {skill.proficiency}%
                      </div>
                      <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                        <div 
                          className="bg-gradient-to-r from-emerald-500 to-blue-500 h-1 rounded-full"
                          style={{ width: `${skill.proficiency}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Learning Activity - Mobile */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Learning Activity</h3>
              <div className="mb-4 overflow-x-auto">
                <div className="grid grid-rows-7 grid-flow-col gap-1 min-w-max">
                  {Array.from({ length: 7 }, (_, day) => 
                    Array.from({ length: 53 }, (_, week) => {
                      const index = week * 7 + day;
                      const dayData = contributionData[index];
                      return dayData ? (
                        <div
                          key={index}
                          className={`w-2.5 h-2.5 rounded-sm ${getIntensityColor(dayData.intensity)} hover:ring-2 hover:ring-emerald-400 transition-all duration-200 cursor-pointer`}
                          title={`${dayData.date}: ${dayData.commits} contributions`}
                        />
                      ) : (
                        <div key={index} className="w-2.5 h-2.5" />
                      );
                    })
                  ).flat()}
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Less</span>
                <div className="flex items-center space-x-1">
                  {[0, 1, 2, 3, 4].map((intensity) => (
                    <div
                      key={intensity}
                      className={`w-2.5 h-2.5 rounded-sm ${getIntensityColor(intensity)}`}
                    />
                  ))}
                </div>
                <span>More</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout: 3-column grid */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-8">
          {/* Left Column - Filters, Activity & Skills */}
          <div className="space-y-6">
            {/* Category Filters - Desktop (moved from top) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Filter by Category</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                      selectedCategory === category
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-600'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {category !== 'all' && getCategoryIcon(category)}
                    <span className="font-medium capitalize">
                      {category} {category !== 'all' && `(${technologies.filter(t => t.category === category).length})`}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contribution Grid - Desktop */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Learning Activity</h3>
              <div className="mb-4 overflow-x-auto">
                <div className="grid grid-rows-7 grid-flow-col gap-1 min-w-max">
                  {Array.from({ length: 7 }, (_, day) => 
                    Array.from({ length: 53 }, (_, week) => {
                      const index = week * 7 + day;
                      const dayData = contributionData[index];
                      return dayData ? (
                        <div
                          key={index}
                          className={`w-3 h-3 rounded-sm ${getIntensityColor(dayData.intensity)} hover:ring-2 hover:ring-emerald-400 transition-all duration-200 cursor-pointer`}
                          title={`${dayData.date}: ${dayData.commits} contributions`}
                        />
                      ) : (
                        <div key={index} className="w-3 h-3" />
                      );
                    })
                  ).flat()}
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Less</span>
                <div className="flex items-center space-x-1">
                  {[0, 1, 2, 3, 4].map((intensity) => (
                    <div
                      key={intensity}
                      className={`w-3 h-3 rounded-sm ${getIntensityColor(intensity)}`}
                    />
                  ))}
                </div>
                <span>More</span>
              </div>
            </div>

            {/* Core Skills - Desktop */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Core Skills</h3>
              <div className="space-y-3">
                {skills.map((skill) => (
                  <div key={skill.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        {skill.icon && skill.icon.startsWith('http') ? (
                          <img 
                            src={skill.icon} 
                            alt={`${skill.name} icon`}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiByeD0iNCIgZmlsbD0iIzZCNzI4MCIvPgo8dGV4dCB4PSIxMiIgeT0iMTYiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPfCfk6c8L3RleHQ+Cjwvc3ZnPg==';
                            }}
                          />
                        ) : (
                          <span className="text-2xl">{skill.icon || '🔧'}</span>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                          {skill.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {skill.category}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-sm text-gray-900 dark:text-white">
                        {skill.proficiency}%
                      </div>
                      <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                        <div 
                          className="bg-gradient-to-r from-emerald-500 to-blue-500 h-1 rounded-full"
                          style={{ width: `${skill.proficiency}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Technology Grid Desktop */}
          <div className="col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Technology Stack
                  {selectedCategory !== 'all' && (
                    <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                      ({selectedCategory})
                    </span>
                  )}
                </h3>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredTechnologies.length} technologies
                </div>
              </div>

              {/* Desktop Technology Grid */}
              <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {filteredTechnologies.map((tech) => (
                  <div
                    key={tech.id}
                    className="group relative p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer"
                    onMouseEnter={() => setHoveredTech(tech.id)}
                    onMouseLeave={() => setHoveredTech(null)}
                  >
                    {/* Tech Icon & Name */}
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        {tech.icon && tech.icon.startsWith('http') ? (
                          <img 
                            src={tech.icon} 
                            alt={`${tech.name} icon`}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzZCNzI4MCIvPgo8dGV4dCB4PSIyMCIgeT0iMjUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPuKapTwvdGV4dD4KPC9zdmc+';
                            }}
                          />
                        ) : (
                          <span className="text-3xl">{tech.icon || '⚡'}</span>
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                        {tech.name}
                      </h4>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {tech.category}
                      </div>
                    </div>

                    {/* Proficiency Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Proficiency</span>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">
                          {tech.proficiency}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full transition-all duration-1000 ease-out"
                          style={{ 
                            width: `${tech.proficiency}%`,
                            backgroundColor: tech.color || '#10b981'
                          }}
                        />
                      </div>
                    </div>

                    {/* Experience */}
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                        <TrendingUp className="w-3 h-3" />
                        <span>{tech.yearsExp}y exp</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {tech.proficiency >= 90 && (
                          <Award className="w-3 h-3 text-yellow-500" title="Expert Level" />
                        )}
                        {tech.proficiency >= 80 && tech.proficiency < 90 && (
                          <Star className="w-3 h-3 text-blue-500" title="Advanced Level" />
                        )}
                      </div>
                    </div>

                    {/* Hover Effect Overlay */}
                    {hoveredTech === tech.id && (
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-xl pointer-events-none" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Category Summary - Desktop */}
            {selectedCategory !== 'all' && (
              <div className="mt-6 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {selectedCategory} Expertise Summary
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Technologies:</span>
                    <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                      {filteredTechnologies.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Avg Proficiency:</span>
                    <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                      {filteredTechnologies.length > 0 
                        ? Math.round(filteredTechnologies.reduce((sum, tech) => sum + tech.proficiency, 0) / filteredTechnologies.length)
                        : 0}%
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Expert Level:</span>
                    <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                      {filteredTechnologies.filter(tech => tech.proficiency >= 90).length}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechMatrix;